import React, { useState, useEffect, useRef } from 'react';
import { PeopleService } from "../service/PeopleService";
import { ProfileService } from "../service/ProfileService";
import { ClientService } from "../service/ClientService";
import classNames from 'classnames';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { AuthorizationService, MANAGE_PEOPLE } from '../service/AuthorizationService';
import { LanguageService } from '../service/LanguageService';
import './Crud.scss'
import * as _ from 'lodash';
import { ProgressBar } from 'primereact/progressbar';
import { v4 as uuidv4 } from 'uuid';
import {Checkbox} from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';

export function PeopleCrud() {

    let emptyPerson = {
        id: uuidv4(),
        name: '',
        email: '',
        password: '',
        superadmin: true,
        active: true
    };

    const [people, setPeople] = useState(null);
    const [clients, setClients] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [isCreation, setIsCreation] = useState(false);
    const [personDialog, setPersonDialog] = useState(false);
    const [deletePersonDialog, setDeletePersonDialog] = useState(false);
    const [person, setPerson] = useState(emptyPerson);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [hasAccess, setHasAccess] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const toast = useRef(null);
    const dt = useRef(null);
    const peopleService = new PeopleService();
    const clientService = new ClientService();
    const profileService = new ProfileService()
    const authorizationService = new AuthorizationService();
    const languageService = new LanguageService();

    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const updateList = async () => {
        const data = await peopleService.list(first, rowsPerPage, search, sortField, sortOrder);
        setPeople(data.items);
        setTotalRecords(data.totalRecords)
    }

    const onPageChange = async (pageEvent) => {
        setFirst(pageEvent.first);
        setRowsPerPage(pageEvent.rows);
        const data = await peopleService.list(pageEvent.first, pageEvent.rows, search, sortField, sortOrder);
        setPeople(data.items);
        setTotalRecords(data.totalRecords)
    }

    const doSearch = async (reset) => {
        const data = await peopleService.list(first, rowsPerPage, reset ? null : globalFilter, sortField, sortOrder);
        setSearch(globalFilter);
        setPeople(data.items);
        setTotalRecords(data.totalRecords)
    }

    const onSort = async (sortEvent) => {
        console.log(sortEvent);
        setSortField(sortEvent.sortField);
        setSortOrder(sortEvent.sortOrder);
        const data = await peopleService.list(first, rowsPerPage, search, sortEvent.sortField, sortEvent.sortOrder);
        setPeople(data.items);
        setTotalRecords(data.totalRecords)
    }

    useEffect(() => {
        setHasAccess(authorizationService.hasPermission(MANAGE_PEOPLE));

        if(authorizationService.hasPermission(MANAGE_PEOPLE)){
            async function fetchData() {
                await updateList();
                
                const clientsData = await clientService.list(-1, -1, null, "name", 1);
                setClients(clientsData.items);

                const profilesData = await profileService.list(-1, -1, null, "name", 1);
                setProfiles(profilesData.items);

                setLoading(false);
            }        
            fetchData();
            
        }
    }, []);

    const openNew = () => {
        emptyPerson.id = uuidv4();
        setIsCreation(true);
        setPerson(emptyPerson);
        setSubmitted(false);
        setPersonDialog(true);
    }

    const hideDialog = () => {
        if(!saving){
            setSubmitted(false);
            setPersonDialog(false);
        }
    }

    const hideDeletePersonDialog = () => {
        setDeletePersonDialog(false);
    }

    const saveDisabled = () => {
        return (person.name || "") === ""
            || (person.email || "") === "";
    }

    const savePerson = async () => {
        try {
            setSaving(true);
            setSubmitted(true);

            if (person.name.trim()) {
                if (!isCreation) {
                    await peopleService.update(person);
                    toast.current.show({ severity: 'success', summary: languageService.getText('Successful'), detail: languageService.getText('Person_Updated'), life: 3000 });
                }
                else {
                    await peopleService.create(person);
                    toast.current.show({ severity: 'success', summary: languageService.getText('Successful'), detail: languageService.getText('Person_Created'), life: 3000 });
                }

                await updateList();
                setPersonDialog(false);
                setPerson(emptyPerson);
                setSaving(false);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: languageService.getText('Error'), detail: error.toString(), sticky: true });
            setSaving(false);
            console.log(error);
            throw error;
        }
    }

    const editPerson = (person) => {
        setIsCreation(false);
        setPerson({ ...person });
        setPersonDialog(true);
    }

    const confirmDeletePerson = (person) => {
        setPerson(person);
        setDeletePersonDialog(true);
    }

    const deletePerson = async () => {
        try {
            setSaving(true);
            await peopleService.delete(person);
            await updateList();
            setDeletePersonDialog(false);
            setPerson(emptyPerson);
            setSaving(false);
            toast.current.show({ severity: 'success', summary: languageService.getText('Successful'), detail: languageService.getText('Person_Deleted'), life: 3000 });    
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _person = { ...person };
        _.set(_person, name, val);
        setPerson(_person);
    }

    const onCheckboxChange = (e, name) => {
        let _person = { ...person };
        _.set(_person, name, e.checked);
        setPerson(_person);
    }


    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label={languageService.getText("New")} icon="pi pi-plus" className="p-button-success p-mr-2" onClick={openNew} />
            </React.Fragment>
        )
    }

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-success p-mr-2" onClick={() => editPerson(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => confirmDeletePerson(rowData)} />
            </React.Fragment>
        );
    }

    const header = (
        <div className="table-header">
            <h5 className="p-m-0">{languageService.getText('Manage_People')}</h5>
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => {setGlobalFilter(e.target.value); if(e.target.value === "") doSearch(true);}} placeholder={`${languageService.getText('Search')}...`}  onKeyDown={(e) => { if (e.key === 'Enter') { doSearch()}}}/>
            </span>
        </div>
    );

    const personDialogFooter = (
        <React.Fragment>
            { !saving && <Button label={languageService.getText("Cancel")} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />}
            { !saving && <Button disabled={saveDisabled()} label={languageService.getText("Save")} icon="pi pi-check" className="p-button-text" onClick={savePerson} />}
            { saving && <ProgressSpinner style={{width: '20px', height: '20px'}} strokeWidth="8"/>}
        </React.Fragment>
    );
    const deletePersonDialogFooter = (
        <React.Fragment>
            { !saving && <Button label={languageService.getText("No")} icon="pi pi-times" className="p-button-text" onClick={hideDeletePersonDialog} />}
            { !saving && <Button label={languageService.getText("Yes")} icon="pi pi-check" className="p-button-text" onClick={deletePerson} />}
            { saving && <ProgressSpinner style={{width: '20px', height: '20px'}} strokeWidth="8"/>}
        </React.Fragment>
    );

    const addUser = () => {
        if(!person.users){
            person.users = [];
        }

        person.users.push({
            id: uuidv4(),
            client_id: '',
            profile_id: '',
            person_id: person.id
        });

        setPerson(person);
    }

    const removeUser = (user_id) => {
        person.users.find(t => t.id === user_id).deleted = true;
        setPerson(Object.assign({}, person));
    }

    const onChangeUserClient = (e, id) => {
        person.users.find(t => t.id === id).client_id = e.target.value;
        console.log(person.users);
        setPerson(Object.assign({}, person));
    }
    const onChangeUserProfile = (e, id) => {
        person.users.find(t => t.id === id).profile_id = e.target.value;
        setPerson(Object.assign({}, person));
    }

    if(hasAccess){

        return (
            <div className="datatable-crud-demo">
                <Toast ref={toast} />
                <div className="card">
                    { loading && <ProgressBar mode="indeterminate"/> }
                    { !loading && <Toolbar className="p-mb-4" left={leftToolbarTemplate}></Toolbar>}

                    { !loading &&
                    <>
                    <DataTable ref={dt} value={people} selection={selectedPerson} onSelectionChange={(e) => setSelectedPerson(e.value)}
                        dataKey="id"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPagePerson RowsPerPageDropdown"
                        currentPagePersonTemplate="Showing {first} to {last} of {totalRecords} people"
                        header={header}
                        autoLayout={true}
                        onSort={onSort}
                        sortField={sortField} 
                        sortOrder={sortOrder}
                        >

                        <Column field="name" header={languageService.getText("Name")} sortable></Column>
                        <Column field="email" header={languageService.getText("E-mail")} sortable></Column>
                        <Column field="superadmin" header="Super-Admin"body={(rowData) => rowData.superadmin ? languageService.getText("Yes") : languageService.getText("No")}></Column>
                        <Column field="active" header={languageService.getText("Active")} body={(rowData) => rowData.active ? languageService.getText("Yes") : languageService.getText("No")}></Column>
                        <Column body={actionBodyTemplate}></Column>
                    </DataTable>
                    <Paginator first={first} rows={rowsPerPage} totalRecords={totalRecords} rowsPerPageOptions={[5, 10, 25, 50, 100]} onPageChange={onPageChange}></Paginator>
                    </>
                    }
                </div>

                <Dialog visible={personDialog} style={{ width: '90vw' }} header={languageService.getText("Person_Details")} modal className="p-fluid" footer={personDialogFooter} onHide={hideDialog}>
                    <div className="p-field">
                        <label htmlFor="name">{languageService.getText("Name")}*</label>
                        <InputText disabled={saving} id="name" value={person.name} onChange={(e) => onInputChange(e, 'name')} required autoFocus className={classNames({ 'p-invalid': submitted && !person.name })} />
                        {submitted && !person.name && <small className="p-invalid">{languageService.getText("Name")} {languageService.getText("is_required")}.</small>}
                    </div>
                    <div className="p-field">
                        <label htmlFor="description">{languageService.getText("E-mail")}*</label>
                        <InputText disabled={saving} id="email" value={person.email} onChange={(e) => onInputChange(e, 'email')} required autoFocus className={classNames({ 'p-invalid': submitted && !person.email })} />
                        {submitted && !person.email && <small className="p-invalid">{languageService.getText("E-mail")} {languageService.getText("is_required")}.</small>}
                    </div>
                    <div className="p-field">
                        <div className="p-formgrid p-grid">
                            <div className="p-field p-col">
                                <label htmlFor="superadmin">Super-Admin</label>
                                <Checkbox id="superadmin" onChange={e => onCheckboxChange(e, 'superadmin')} checked={person.superadmin} style={{marginLeft: '20px'}}></Checkbox>
                            </div>
                            <div className="p-field p-col">
                                <label htmlFor="password">{languageService.getText("Active")}*</label>
                                <Checkbox id="active" onChange={e => onCheckboxChange(e, 'active')} checked={person.active} style={{marginLeft: '20px'}}></Checkbox>
                            </div>
                        </div>
                    </div>
                    { !person.superadmin && 
                    <div className="p-field">
                        <label className="p-mb-3">{languageService.getText("Users")}</label>
                        <div className="p-formgrid p-grid">
                        <DataTable 
                            ref={dt} 
                            value={(person.users || []).filter(t => !t.deleted)}
                            dataKey="id"
                            className="editable-cells-table"
                            footer={<Button icon="pi pi-plus" className="p-button-rounded p-button-success p-mr-2" onClick={() => addUser()} />}>

                            <Column field="client_id" header={languageService.getText("Client")} body={(rowData) => 
                                <Dropdown optionLabel="name" optionValue="id" value={rowData.client_id} options={clients} style={{ width: '100%' }} onChange={(e) => onChangeUserClient(e, rowData.id)} placeholder="Select a client"/>
                            }></Column>
                            <Column field="profile_id" header={languageService.getText("Profile")} body={(rowData) => 
                                <Dropdown optionLabel="name" optionValue="id" value={rowData.profile_id} options={profiles} onChange={(e) => onChangeUserProfile(e, rowData.id)} placeholder="Select a profile"/>
                            }></Column>
                            <Column field="id" header=""  style={{width:'100px'}} body={(rowData) => 
                                <Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => removeUser(rowData.id)} />
                            }/>
                        </DataTable>
                        
                        </div>
                    </div>
                    }

                    <div style={{height: '70px'}}></div>

                </Dialog>

                <Dialog visible={deletePersonDialog} style={{ width: '450px' }} header={languageService.getText("Confirm")} modal footer={deletePersonDialogFooter} onHide={hideDeletePersonDialog}>
                    <div className="confirmation-content">
                        <i className="pi pi-exclamation-triangle p-mr-3" style={{ fontSize: '2rem' }} />
                        {person && <span>{languageService.getText("Are_you_sure_you_want_to_delete")} <b>{person.name}</b>?</span>}
                    </div>
                </Dialog>
            </div>
        );
    }
    else {
        return (
            <div className="datatable-crud-demo">
                <div className="card">
                    <b>Permission Denied</b>
                </div>
            </div>
        );
    }
}